import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Button from '../Button';
import { CARRIER_LABELS, DIRECTIONS, SHIPMENT_STATUSES } from '../../constants/shipments';
import { CODES, shipmentsApi } from '../../utils/shipmentsApi';
import Dialog from './Dialog';
import EntityPicker from './EntityPicker';
import { SelectField, TextAreaField, TextField } from './FormFields';
import { Banner } from './Ui';
import { useRemote } from './useRemote';
import { EMPTY_FORM, buildCreateBody } from './shipmentForm';

// Manual add of a shipment (POST /). `initial` prefills the form, `submit` replaces the request (review: "Vytvořit zásilku").
const AddShipmentDialog = ({ onClose, onCreated, initial, submit = shipmentsApi.create, title = 'Přidat zásilku', submitLabel = 'Přidat zásilku' }) => {
  const [form, setForm] = useState({ ...EMPTY_FORM, ...initial });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const carriers = useRemote('carriers', () => shipmentsApi.carriers());

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const setId = (key) => (id) => setForm((f) => ({ ...f, [key]: id }));
  const carrierOptions = carriers.data?.length
    ? carriers.data.map((c) => ({ value: c.code, label: c.name || CARRIER_LABELS[c.code] || c.code }))
    : Object.entries(CARRIER_LABELS).map(([value, label]) => ({ value, label }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const result = await submit(buildCreateBody(form));
      onCreated(result);
    } catch (err) {
      setError(err);
      setSaving(false);
    }
  };

  const duplicateId = error?.code === CODES.CONFLICT ? Number(error.data?.id) : 0;

  return (
    <Dialog
      title={title} wide busy={saving} onClose={onClose}
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>Zrušit</Button>
          <Button type="submit" form="shp-add-form" loading={saving}>{submitLabel}</Button>
        </>
      )}
    >
      <form id="shp-add-form" onSubmit={handleSubmit} noValidate className="shp-stack" style={{ paddingBottom: 8 }}>
        {error && (
          <Banner tone="bad">
            {duplicateId > 0 ? 'Zásilka s tímto číslem už existuje.' : error.message}
            {duplicateId > 0 && <> <Link to={`/zasilky/${duplicateId}`} onClick={onClose} className="shp-link">Otevřít existující zásilku</Link></>}
          </Banner>
        )}
        <div className="shp-grid2">
          <SelectField label="Směr" required value={form.direction} onChange={set('direction')} options={DIRECTIONS} />
          <SelectField label="Stav" required value={form.status} onChange={set('status')} options={SHIPMENT_STATUSES.map((s) => ({ value: s.value, label: s.label }))} />
          <SelectField label="Dopravce" value={form.carrierCode} onChange={set('carrierCode')} placeholder="Neznámý / nevybráno" options={carrierOptions} />
          <TextField label="Číslo zásilky" value={form.trackingNumber} onChange={set('trackingNumber')} maxLength={80} autoComplete="off" hint="Nepovinné – zásilka bez čísla bude čekat na tracking." />
          <TextField label="Číslo objednávky" value={form.orderRef} onChange={set('orderRef')} maxLength={80} autoComplete="off" />
          <TextField label="Očekávané doručení" type="date" value={form.expectedDelivery} onChange={set('expectedDelivery')} />
          <TextField label="Odesílatel" value={form.senderName} onChange={set('senderName')} maxLength={200} autoComplete="off" />
          <TextField label="Příjemce" value={form.recipientName} onChange={set('recipientName')} maxLength={200} autoComplete="off" />
          <TextAreaField className="shp-full" label="Popis zboží" value={form.description} onChange={set('description')} maxLength={1000} rows={2} />
          <EntityPicker kind="customer" label="Zákazník" value={form.customerId} onChange={setId('customerId')} />
          <EntityPicker kind="supplier" label="Dodavatel" value={form.supplierId} onChange={setId('supplierId')} />
          <EntityPicker kind="machine" label="Trenažér" value={form.machineId} onChange={setId('machineId')} />
          <EntityPicker kind="order" label="Objednávka" value={form.orderBatchId} onChange={setId('orderBatchId')} />
          <TextAreaField className="shp-full" label="Poznámka" value={form.note} onChange={set('note')} maxLength={1000} rows={2} hint="Uloží se do historie zásilky." />
        </div>
      </form>
    </Dialog>
  );
};

export default AddShipmentDialog;
